import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select as MuiSelect,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Checkbox from "components/common/Checkbox";
import { Calendar, ExternalLink, Plus, Trash2 } from "lucide-react";
import {
  CreateSystemScheduleRequest,
  Schedule,
} from "protogen/scheduling_service_pb";
import { Report } from "protogen/reports_service_pb";
import {
  useCreateSystemSchedule,
  useListSystemSchedules,
} from "services/scheduling";
import TextField from "components/common/TextField";
import Select from "components/common/Select";
import AddressInputList from "components/email/AddressInputList";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { DatePicker } from "@mui/x-date-pickers";

type SchedulePayload = {
  task_type: string;
  method: string;
  title?: string;
  payload?: {
    reportId: number;
    reportParamsJson?: string;
    sheetOutputs?: Array<{
      sheetId: string;
      range: string;
      replace?: boolean;
    }>;
    emailRecipients?: string[];
  };
};

type SheetOutput = {
  sheetId: string;
  range: string;
  mode: "append" | "replace";
};

const RECURRING_UNITS = ["hour", "day", "week", "month"];

const parseSchedulePayload = (payloadJson: string): SchedulePayload | null => {
  try {
    return JSON.parse(payloadJson) as SchedulePayload;
  } catch {
    return null;
  }
};

const getScheduleTitle = (schedule: Schedule): string => {
  const payload = parseSchedulePayload(schedule.payloadJson);
  return payload?.title || "";
};

const getCadenceDisplay = (schedule: Schedule): string => {
  if (!schedule.recurringUnit || schedule.recurringInterval <= 0) {
    return "One-time";
  }
  if (schedule.recurringInterval === 1) {
    return `Every ${schedule.recurringUnit}`;
  }
  return `Every ${schedule.recurringInterval} ${schedule.recurringUnit}s`;
};

const getEmailRecipients = (schedule: Schedule): string[] => {
  const payload = parseSchedulePayload(schedule.payloadJson);
  return payload?.payload?.emailRecipients || [];
};

const getSheetOutputs = (
  schedule: Schedule,
): Array<{ sheetId: string; range: string }> => {
  const payload = parseSchedulePayload(schedule.payloadJson);
  return payload?.payload?.sheetOutputs || [];
};

const extractSheetId = (input: string): string | null => {
  if (
    input.includes("docs.google.com") ||
    input.includes("sheets.google.com")
  ) {
    return null;
  }
  const trimmed = input.trim();
  if (/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return trimmed;
  }
  return null;
};

type ScheduledJobItemProps = {
  schedule: Schedule;
};

function ScheduledJobItem({ schedule }: ScheduledJobItemProps) {
  const sheetOutputs = getSheetOutputs(schedule);
  const emailRecipients = getEmailRecipients(schedule);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: 1.5,
        bgcolor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Cadence: {getCadenceDisplay(schedule)}
        </Typography>
        <Link
          href={`/tools/schedule-management?ref=${schedule.ref}`}
          sx={{ fontSize: "12px" }}
        >
          Edit
        </Link>
      </Box>
      {getScheduleTitle(schedule) && (
        <Typography variant="bodyHeavy">
          {getScheduleTitle(schedule)}
        </Typography>
      )}
      {emailRecipients.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Recipients: {emailRecipients.join(", ")}
        </Typography>
      )}
      {sheetOutputs.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
          {sheetOutputs.map((output) => (
            <Link
              key={output.sheetId}
              href={`https://docs.google.com/spreadsheets/d/${output.sheetId}`}
              target="_blank"
              rel="noopener"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "12px",
              }}
            >
              <ExternalLink size={12} />
              Sheet
            </Link>
          ))}
        </Box>
      )}
    </Box>
  );
}

type AddSyncDialogProps = {
  open: boolean;
  onClose: () => void;
  reportId: number;
  report: Report | null;
  initialParameters: Record<string, any>;
  onCreated: (scheduleRef: string) => void;
};

function AddSyncDialog({
  open,
  onClose,
  reportId,
  report,
  initialParameters,
  onCreated,
}: AddSyncDialogProps) {
  const [title, setTitle] = useState("");
  const { pushSnack } = useSnackpack();
  const [localParameters, setLocalParameters] = useState<Record<string, any>>(
    {},
  );
  const [recurringUnit, setRecurringUnit] = useState("day");
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [sheetOutputs, setSheetOutputs] = useState<SheetOutput[]>([
    { sheetId: "", range: "", mode: "replace" },
  ]);
  const [sheetErrors, setSheetErrors] = useState<
    Record<number, { sheetId?: string; range?: string }>
  >({});
  const [formError, setFormError] = useState<string | null>(null);

  const { request: createSchedule, loading } = useCreateSystemSchedule();

  useEffect(() => {
    if (open) {
      setLocalParameters({ ...initialParameters });
    }
  }, [open, initialParameters]);

  const resetForm = () => {
    setTitle("");
    setLocalParameters({ ...initialParameters });
    setRecurringUnit("day");
    setRecurringInterval(1);
    setEmailRecipients([]);
    setSheetOutputs([{ sheetId: "", range: "", mode: "replace" }]);
    setSheetErrors({});
    setFormError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateSheetOutputs = (): boolean => {
    const errors: Record<number, { sheetId?: string; range?: string }> = {};
    let valid = true;
    const errorMessages: string[] = [];

    sheetOutputs.forEach((output, idx) => {
      const hasSheetId = output.sheetId.trim();
      const hasRange = output.range.trim();

      if (!hasSheetId && !hasRange) {
        return;
      }

      const fieldErrors: { sheetId?: string; range?: string } = {};

      if (hasSheetId) {
        const extracted = extractSheetId(output.sheetId);
        if (extracted === null) {
          fieldErrors.sheetId = "Enter a Sheet ID, not a URL";
          valid = false;
        }
      }

      if (hasSheetId && !hasRange) {
        fieldErrors.range = "Range is required";
        valid = false;
      }

      if (Object.keys(fieldErrors).length > 0) {
        errors[idx] = fieldErrors;
      }
    });

    setSheetErrors(errors);

    if (!valid && Object.keys(errors).length > 0) {
      errorMessages.push("Please fix the errors in sheet outputs");
    }

    setFormError(errorMessages.length > 0 ? errorMessages.join(". ") : null);
    return valid;
  };

  const handleAddSheet = () => {
    setSheetOutputs([
      ...sheetOutputs,
      { sheetId: "", range: "", mode: "replace" },
    ]);
  };

  const handleRemoveSheet = (idx: number) => {
    setSheetOutputs(sheetOutputs.filter((_, i) => i !== idx));
    const newErrors = { ...sheetErrors };
    delete newErrors[idx];
    setSheetErrors(newErrors);
  };

  const handleSheetChange = (
    idx: number,
    field: keyof SheetOutput,
    value: string | boolean,
  ) => {
    const updated = [...sheetOutputs];
    updated[idx] = { ...updated[idx], [field]: value };
    setSheetOutputs(updated);

    if ((field === "sheetId" || field === "range") && sheetErrors[idx]) {
      const newErrors = { ...sheetErrors };
      if (newErrors[idx]) {
        delete newErrors[idx][field];
        if (Object.keys(newErrors[idx]).length === 0) {
          delete newErrors[idx];
        }
      }
      setSheetErrors(newErrors);
      setFormError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateSheetOutputs()) {
      return;
    }

    const validSheetOutputs = sheetOutputs
      .filter((s) => s.sheetId.trim() && s.range.trim())
      .map((s) => ({
        sheetId: s.sheetId.trim(),
        range: s.range.trim(),
        replace: s.mode === "replace",
      }));

    const serializeParameters = (params: Record<string, any>): string => {
      const serialized: Record<string, any> = {};
      for (const [key, value] of Object.entries(params)) {
        if (value instanceof Date) {
          serialized[key] = value.toISOString();
        } else {
          serialized[key] = value;
        }
      }
      return JSON.stringify(serialized);
    };

    const payload: SchedulePayload = {
      task_type: "protogen.GenericReportExecution",
      method: "protogen.GenericReportExecution",
      title: title.trim() || undefined,
      payload: {
        reportId,
        reportParamsJson: serializeParameters(localParameters),
        sheetOutputs:
          validSheetOutputs.length > 0 ? validSheetOutputs : undefined,
        emailRecipients:
          emailRecipients.length > 0 ? emailRecipients : undefined,
      },
    };

    const deadline = new Date();
    deadline.setHours(deadline.getHours() + 1);

    const resp = await createSchedule(
      new CreateSystemScheduleRequest({
        deadlineSec: BigInt(Math.floor(deadline.getTime() / 1000)),
        payloadJson: JSON.stringify(payload),
        recurringUnit,
        recurringInterval,
      }),
    );

    if (resp?.schedule) {
      onCreated(resp.schedule.ref);
    }
  };

  const isValid =
    recurringInterval > 0 &&
    (sheetOutputs.some((s) => s.sheetId.trim()) || emailRecipients.length > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Scheduled Sync</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            placeholder="e.g., Weekly family report sync"
          />

          {report && report.explicitParameters.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Report Parameters
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {report.explicitParameters.map((param) => (
                  <Box key={param.name}>
                    {param.type === "date" || param.type === "month" ? (
                      <DatePicker
                        label={param.label}
                        value={localParameters[param.name] || null}
                        format={param.type === "month" ? "MM/yyyy" : undefined}
                        views={
                          param.type === "month" ? ["year", "month"] : undefined
                        }
                        openTo={param.type === "month" ? "month" : undefined}
                        onChange={(date) =>
                          setLocalParameters((prev) => ({
                            ...prev,
                            [param.name]: date,
                          }))
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                          },
                        }}
                        sx={{
                          ".MuiInputAdornment-root": {
                            marginRight: "12px",
                          },
                        }}
                      />
                    ) : param.type === "choice" ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>{param.label}</InputLabel>
                        <MuiSelect
                          label={param.label}
                          value={localParameters[param.name] || ""}
                          onChange={(e) =>
                            setLocalParameters((prev) => ({
                              ...prev,
                              [param.name]: e.target.value,
                            }))
                          }
                        >
                          {param.choices.map((choice) => (
                            <MenuItem key={choice} value={choice}>
                              {choice}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                    ) : param.type === "boolean" ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localParameters[param.name] !== "0"}
                            onChange={(_, checked) =>
                              setLocalParameters((prev) => ({
                                ...prev,
                                [param.name]: checked ? "1" : "0",
                              }))
                            }
                          />
                        }
                        label={param.label}
                      />
                    ) : (
                      <TextField
                        label={param.label}
                        value={localParameters[param.name] || ""}
                        onChange={(e) =>
                          setLocalParameters((prev) => ({
                            ...prev,
                            [param.name]: e.target.value,
                          }))
                        }
                        fullWidth
                        size="small"
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <Select
                variant="outlined"
                label="Frequency"
                value={recurringUnit}
                onChange={(e) => setRecurringUnit(e.target.value)}
              >
                {RECURRING_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Every"
              type="number"
              value={recurringInterval}
              onChange={(e) =>
                setRecurringInterval(Math.max(1, parseInt(e.target.value) || 1))
              }
              sx={{ width: "100px" }}
              inputProps={{ min: 1 }}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Email Recipients
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
                minHeight: "48px",
              }}
            >
              <AddressInputList
                recipients={emailRecipients}
                setRecipients={setEmailRecipients}
                placeholder="Add email addresses..."
              />
            </Box>
          </Box>

          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Google Sheet Outputs
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={handleAddSheet}
                startIcon={<Plus size={14} />}
              >
                Add Sheet
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Syned sheets must be shared with{" "}
              <strong
                style={{ cursor: "pointer" }}
                onClick={() => {
                  navigator.clipboard.writeText(
                    "server-api-access@internal-sheets-manager.iam.gserviceaccount.com",
                  );
                  pushSnack({
                    message: "Copied to clipboard",
                    key: "Copied to clipboard",
                  });
                }}
              >
                server-api-access@internal-sheets-manager.iam.gserviceaccount.com
              </strong>
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {sheetOutputs.map((output, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 1.5,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                  >
                    <TextField
                      label="Sheet ID"
                      value={output.sheetId}
                      onChange={(e) =>
                        handleSheetChange(idx, "sheetId", e.target.value)
                      }
                      error={!!sheetErrors[idx]?.sheetId}
                      helperText={sheetErrors[idx]?.sheetId}
                      fullWidth
                      placeholder="e.g., 1jOdLtr3l_Nahw3pnDvDGY..."
                    />
                    {sheetOutputs.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSheet(idx)}
                        sx={{ mt: 1 }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      label="Range"
                      value={output.range}
                      onChange={(e) =>
                        handleSheetChange(idx, "range", e.target.value)
                      }
                      error={!!sheetErrors[idx]?.range}
                      helperText={sheetErrors[idx]?.range}
                      placeholder="e.g., Sheet1!A1:ZZ"
                      sx={{ flex: 1, minWidth: "150px" }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <ToggleButtonGroup
                        value={output.mode}
                        exclusive
                        onChange={(_, newMode) => {
                          if (newMode) handleSheetChange(idx, "mode", newMode);
                        }}
                        size="small"
                      >
                        <ToggleButton value="replace">Replace</ToggleButton>
                        <ToggleButton value="append">Append</ToggleButton>
                      </ToggleButtonGroup>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "12px", mt: 0.5 }}
                      >
                        {output.mode === "replace"
                          ? "Replaces existing data"
                          : "Adds new rows below data"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isValid}
        >
          Create Sync
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type ScheduledJobsListProps = {
  reportId: number;
  report: Report | null;
  parameters: Record<string, any>;
};

export default function ScheduledJobsList({
  reportId,
  report,
  parameters,
}: ScheduledJobsListProps) {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { request: listSchedules, loading } = useListSystemSchedules();

  useEffect(() => {
    if (!reportId) {
      setSchedules([]);
      return;
    }
    listSchedules({
      includeCanceled: false,
      taskType: "protogen.GenericReportExecution",
    }).then((resp) => {
      if (!resp) return;
      const filtered = resp.schedules.filter((schedule) => {
        const payload = parseSchedulePayload(schedule.payloadJson);
        return (
          payload?.payload?.reportId === reportId &&
          schedule.status === "upcoming"
        );
      });
      setSchedules(filtered);
    });
  }, [reportId]);

  const handleCreated = (scheduleRef: string) => {
    setDialogOpen(false);
    navigate(`/tools/schedule-management?ref=${scheduleRef}`);
  };

  if (loading) {
    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Loading scheduled syncs...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Calendar size={16} />
          <Typography variant="body" sx={{ fontWeight: 500 }}>
            Scheduled Syncs {schedules.length > 0 && `(${schedules.length})`}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={() => setDialogOpen(true)}
          startIcon={<Plus size={14} />}
        >
          Add sync
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 2,
          bgcolor: "grey.50",
          borderRadius: 1,
        }}
      >
        {schedules.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No scheduled syncs for this report.
          </Typography>
        ) : (
          schedules.map((schedule) => (
            <ScheduledJobItem key={schedule.ref} schedule={schedule} />
          ))
        )}
      </Box>

      <AddSyncDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        reportId={reportId}
        report={report}
        initialParameters={parameters}
        onCreated={handleCreated}
      />
    </Box>
  );
}
